import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmacaoModalComponent } from '../components/confirmacao-modal/confirmacao-modal.component';

interface AcaoConfirmacao {
  label: string;
  action: () => void;
}

interface OpcoesConfirmacao {
  titulo: string;
  mensagem: string;
  confirmLabel: string;
  cancelLabel: string | null;
  icone?: 'question' | 'warning';
}

/**
 * Diálogos de confirmação no padrão gov.br (br-modal via NgbModal). Espelha a API do
 * AlertService (confirm/confirmation/warningThen) para substituir os diálogos SweetAlert.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmacaoModalService {
  private readonly modal = inject(NgbModal);

  /** Confirmação simples (Sim/Não). Executa o callback ao confirmar. */
  confirm(mensagem: string, onConfirm: () => void): void {
    this.abrir({ titulo: 'Confirmação', mensagem, confirmLabel: 'Sim', cancelLabel: 'Não' })
      .then((ok) => { if (ok) onConfirm(); });
  }

  /** Confirmação com título e rótulos customizados. */
  confirmation(
    titulo: string,
    mensagem: string,
    confirmAction: AcaoConfirmacao,
    cancelAction?: AcaoConfirmacao,
  ): void {
    this.abrir({
      titulo,
      mensagem,
      confirmLabel: confirmAction.label,
      cancelLabel: cancelAction?.label ?? 'Cancelar',
    }).then((ok) => {
      if (ok) {
        confirmAction.action();
      } else if (cancelAction?.action) {
        cancelAction.action();
      }
    });
  }

  /**
   * Confirmação que devolve a escolha em vez de executar callback.
   * Uso principal: guards `CanDeactivate`, que precisam do booleano.
   *
   * Abre sem dispensa implícita (ESC/backdrop): o chamador pode inverter o significado
   * do booleano sem que um fechamento acidental dispare a opção destrutiva.
   */
  confirmarAsync(
    titulo: string,
    mensagem: string,
    confirmLabel: string,
    cancelLabel: string,
  ): Promise<boolean> {
    return this.abrir({ titulo, mensagem, confirmLabel, cancelLabel }, true);
  }

  /** Aviso de um botão; executa o callback ao fechar. */
  warningThen(mensagem: string, callback: () => void): void {
    this.abrir({ titulo: 'Atenção!', mensagem, confirmLabel: 'OK', cancelLabel: null, icone: 'warning' })
      .then(() => callback(), () => callback());
  }

  private abrir(opcoes: OpcoesConfirmacao, bloquearDispensa = false): Promise<boolean> {
    const ref = this.modal.open(ConfirmacaoModalComponent, {
      centered: true,
      ...(bloquearDispensa ? { backdrop: 'static' as const, keyboard: false } : {}),
    });
    Object.assign(ref.componentInstance, opcoes);
    return ref.result.then((r) => r === true, () => false);
  }
}
